import React from 'react'
import { Button, Checkbox, Divider, useTheme } from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'

import { Container, Finder, Selector, SideDrawer } from '../../../components'
import { FinderSelection } from '../../../hooks'
import { Folder, InventoryConfig } from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'

export enum AttachmentFilterValue {
    NA = 0,
    HAS_ATTACHMENTS = 1,
    NO_ATTACHMENTS = 2,
}

export enum WorkorderFilterValue {
    NA = 0,
    HAS_WORKORDERS = 1,
    NO_WORKORDERS = 2,
}

export enum HandledFilterValue {
    NA = 0,
    HANDLED = 1,
    NOT_HANDLED = 2,
}

export interface FlagInspectionFilter {
    inventoryConfigId: number
    attachmentFilter: AttachmentFilterValue
    workorderFilter: WorkorderFilterValue
    handledFilter: HandledFilterValue
    modified: boolean
}

export const defaultFlagInspectionFilter: FlagInspectionFilter = {
    inventoryConfigId: -1,
    attachmentFilter: AttachmentFilterValue.NA,
    workorderFilter: WorkorderFilterValue.NA,
    handledFilter: HandledFilterValue.NOT_HANDLED,
    modified: false,
}

interface Props {
    open: boolean
    tree: Folder
    selection: FinderSelection
    filterState: FlagInspectionFilter
    inventoryConfigList: InventoryConfig[]
    setFilterState: (newValue: FlagInspectionFilter) => void
    onApplyFilter: (
        modifiedFilter: FlagInspectionFilter,
        finderSelection?: FinderSelection,
    ) => void
    onClose: () => void
}

export const FlagInspectionFilterDrawer: React.FC<Props> = ({
    open,
    onClose,
    tree,
    selection,
    filterState,
    setFilterState,
    onApplyFilter,
    inventoryConfigList,
}) => {
    const theme = useTheme()

    const selectorStyle: React.CSSProperties = {
        margin: theme.spacing(1, 0),
    }

    return (
        <SideDrawer
            open={open}
            handleClose={onClose}
            width={330}
            title="Filters"
        >
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                {/* Filter Content */}
                <Container
                    style={{
                        flexDirection: 'column',
                        padding: theme.spacing(2),
                        height: 'calc(100% - 70px)',
                        overflowY: 'auto',
                    }}
                >
                    {/* Inventory Config Filter */}
                    <Selector
                        data={inventoryConfigList}
                        customStyle={{ formControl: selectorStyle }}
                        label="Inventory Item"
                        onChange={(e) => {
                            setFilterState({
                                ...filterState,
                                inventoryConfigId: e.target.value as number,
                                modified: true,
                            })
                        }}
                        currentValue={filterState.inventoryConfigId}
                        getDisplayString={(inv) => inv.name}
                        searchable
                        variant="outlined"
                        size="small"
                    />

                    {/* Attachments Filter */}
                    <Container
                        style={{
                            flexDirection: 'column',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                fontWeight: 600,
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Attachments
                        </span>
                        <ToggleButtonGroup
                            value={filterState.attachmentFilter}
                            exclusive
                            onChange={(_, value) => {
                                if (value !== null) {
                                    setFilterState({
                                        ...filterState,
                                        attachmentFilter: value,
                                        modified: true,
                                    })
                                }
                            }}
                            size="small"
                            style={{ width: '100%' }}
                        >
                            <ToggleButton
                                value={AttachmentFilterValue.NA}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                All
                            </ToggleButton>
                            <ToggleButton
                                value={AttachmentFilterValue.HAS_ATTACHMENTS}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Has
                            </ToggleButton>
                            <ToggleButton
                                value={AttachmentFilterValue.NO_ATTACHMENTS}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                None
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Container>

                    {/* Workorders Filter */}
                    <Container
                        style={{
                            flexDirection: 'column',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                fontWeight: 600,
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Workorders
                        </span>
                        <ToggleButtonGroup
                            value={filterState.workorderFilter}
                            exclusive
                            onChange={(_, value) => {
                                if (value !== null) {
                                    setFilterState({
                                        ...filterState,
                                        workorderFilter: value,
                                        modified: true,
                                    })
                                }
                            }}
                            size="small"
                            style={{ width: '100%' }}
                        >
                            <ToggleButton
                                value={WorkorderFilterValue.NA}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                All
                            </ToggleButton>
                            <ToggleButton
                                value={WorkorderFilterValue.HAS_WORKORDERS}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Has
                            </ToggleButton>
                            <ToggleButton
                                value={WorkorderFilterValue.NO_WORKORDERS}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                None
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Container>

                    {/* Handled Filter */}
                    <Container
                        style={{
                            flexDirection: 'column',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                fontWeight: 600,
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Handled
                        </span>
                        <ToggleButtonGroup
                            value={filterState.handledFilter}
                            exclusive
                            onChange={(_, value) => {
                                if (value !== null) {
                                    setFilterState({
                                        ...filterState,
                                        handledFilter: value,
                                        modified: true,
                                    })
                                }
                            }}
                            size="small"
                            style={{ width: '100%' }}
                        >
                            <ToggleButton
                                value={HandledFilterValue.NA}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                All
                            </ToggleButton>
                            <ToggleButton
                                value={HandledFilterValue.HANDLED}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Handled
                            </ToggleButton>
                            <ToggleButton
                                value={HandledFilterValue.NOT_HANDLED}
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Not Handled
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Container>

                    <Divider style={{ margin: theme.spacing(2, 0) }} />

                    {/* Location Filter */}
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                            fontWeight: 600,
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        Location
                    </span>

                    <Container
                        style={{ flex: 1, overflow: 'auto', minHeight: 250 }}
                    >
                        <Finder
                            root={tree}
                            locationSelection={selection.selection}
                            onClickFolder={(folder) =>
                                selection.setFinderSelection(folder)
                            }
                            onClickUnit={(unit) =>
                                selection.setFinderSelection(unit)
                            }
                            selectedIcon={<></>}
                            getUnitAdornment={(unit) => {
                                return (
                                    <Container style={{ flex: 1 }}>
                                        <div style={{ flex: 1 }} />
                                        <Checkbox
                                            color="primary"
                                            checked={
                                                selection.selection.unit[
                                                    unit.id
                                                ] !== undefined
                                            }
                                            onClick={() => {
                                                selection.setFinderSelection({
                                                    type: 'unit',
                                                    location: unit,
                                                })
                                                setFilterState({
                                                    ...filterState,
                                                    modified: true,
                                                })
                                            }}
                                        />
                                    </Container>
                                )
                            }}
                            getFolderAdornment={(folder) => {
                                return (
                                    <Container style={{ flex: 1 }}>
                                        <div style={{ flex: 1 }} />
                                        <Checkbox
                                            color="primary"
                                            checked={
                                                selection.selection.folder[
                                                    folder.id
                                                ] !== undefined
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                selection.setFinderSelection({
                                                    type: 'folder',
                                                    location: folder,
                                                })
                                                setFilterState({
                                                    ...filterState,
                                                    modified: true,
                                                })
                                            }}
                                        />
                                    </Container>
                                )
                            }}
                        />
                    </Container>
                </Container>

                {/* Footer Buttons */}
                <Divider />
                <Container
                    style={{
                        padding: theme.spacing(2),
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        color="secondary"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            selection.setFinderSelection(undefined)
                            onApplyFilter(defaultFlagInspectionFilter)
                        }}
                        style={{ textTransform: 'none' }}
                    >
                        Clear All
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        size="small"
                        disabled={!filterState.modified}
                        onClick={() => {
                            onApplyFilter(filterState, selection)
                        }}
                        style={{ textTransform: 'none' }}
                    >
                        Apply Filters
                    </Button>
                </Container>
            </Container>
        </SideDrawer>
    )
}
